import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  X,
  RotateCcw,
  LockKeyhole,
  CircleChevronDown,
} from 'lucide-react';
import {
  hostConnectionQueryOptions,
  rotateSecret,
  revealSecret,
  reinitiateOAuth,
} from '../../lib/queries/customers';
import NetSuiteSigningKey from './NetSuiteSigningKey';
import NetSuiteRouting from './NetSuiteRouting';

function formatTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export default function NetSuite({ id }: { id: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(hostConnectionQueryOptions(id));

  const [rotating, setRotating] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [reinitLoading, setReinitLoading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  async function handleRotate() {
    if (
      !confirm(
        'Rotating invalidates all existing webhook signatures immediately. Continue?',
      )
    )
      return;
    setRotating(true);
    try {
      const result = await rotateSecret(id);
      setNewSecret(result.secret);
      qc.invalidateQueries({ queryKey: ['host-connection', id] });
    } finally {
      setRotating(false);
    }
  }

  async function handleCopy() {
    try {
      const result = await revealSecret(id);
      await navigator.clipboard.writeText(result.secret);
    } catch {
      alert('Failed to copy secret.');
    }
  }

  async function handleReinitiate() {
    setReinitLoading(true);
    try {
      await reinitiateOAuth(id);
      qc.invalidateQueries({ queryKey: ['host-connection', id] });
    } finally {
      setReinitLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-lg bg-white/6" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const expiresIn = daysUntil(data.oauth.expiresAt);
  const oauthConnected = data.oauth.status === 'connected';
  const oauthPending = data.oauth.status === 'pending';

  return (
    <div className="space-y-4">
      {/* OAuth Credentials */}
      <div className="bg-bg-elevated border-primary/8 rounded-lg border p-5">
        <span className="text-subtle text-xs font-semibold tracking-widest uppercase">
          OAuth Credentials
        </span>
        <div className="bg-bg mt-4 flex flex-col justify-between gap-5 rounded-lg p-5 md:flex-row md:items-center md:gap-3">
          <div className="flex items-center gap-3">
            <div
              className={[
                'flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-full',
                oauthConnected ? 'bg-success' : 'bg-primary/8',
              ].join(' ')}
            >
              {oauthConnected ? (
                <Check size={10} className="text-primary" />
              ) : (
                <X size={10} className="text-muted" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-primary text-sm font-medium">
                OAuth 2.0 access token
              </p>
              {oauthConnected && data.oauth.refreshedAt && (
                <p className="text-subtle mt-0.5 text-xs">
                  Connected · refreshed {formatTs(data.oauth.refreshedAt)} UTC
                  {expiresIn !== null && ` · expires in ${expiresIn} days`}
                </p>
              )}
              {oauthPending && (
                <p className="text-muted mt-0.5 text-xs">
                  Pending — waiting for host to complete OAuth flow
                </p>
              )}
              {data.oauth.status === 'disconnected' && (
                <p className="text-muted mt-0.5 text-xs">Not connected</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {oauthConnected && (
              <span className="border-success flex items-center gap-1.5 rounded border px-3 py-1 text-[0.625rem] text-green-400">
                <span className="bg-success h-1.5 w-1.5 rounded-full" />
                Connected
              </span>
            )}
            <button
              onClick={handleReinitiate}
              disabled={reinitLoading || oauthPending}
              className="text-primary/70 hover:text-primary border-primary/10 rounded border px-3 py-1 text-[0.625rem] transition-colors disabled:opacity-40"
            >
              {reinitLoading ? 'Initiating…' : 'Re-initiate'}
            </button>
          </div>
        </div>
      </div>

      {/* Signing Secret */}
      <div className="bg-bg-elevated border-primary/8 rounded-lg border p-5">
        <div className="flex items-center justify-between">
          <span className="text-subtle text-xs font-semibold tracking-widest uppercase">
            Signing Secret
          </span>
          <button
            onClick={handleRotate}
            disabled={rotating}
            title="Rotate signing secret"
            className="text-danger border-danger hover:text-primary rounded-sm border p-1.5 transition-colors disabled:opacity-40"
          >
            <RotateCcw size={16} />
          </button>
        </div>

        {newSecret ? (
          <div className="mt-3 rounded border border-yellow-500/30 bg-yellow-500/5 p-3">
            <p className="mb-1 text-xs font-medium text-yellow-300">
              New secret — copy it now, it won't be shown again
            </p>
            <code className="text-primary text-xs break-all">{newSecret}</code>
          </div>
        ) : (
          <div className="border-primary/8 mt-3 flex items-center justify-between gap-2 rounded border p-4">
            <div className="flex gap-2">
              <LockKeyhole className="text-muted" size={14} />
              <span className="text-primary/20 max-w-36 flex-1 overflow-hidden font-mono text-xs md:max-w-full">
                {data.signingSecret.maskedValue ?? 'No secret provisioned'}
              </span>
            </div>

            {data.signingSecret.exists && (
              <button
                onClick={handleCopy}
                className="rounded border border-white/10 px-2.5 py-1 text-xs text-white/70 transition-colors hover:text-white"
              >
                Copy
              </button>
            )}
          </div>
        )}

        {data.signingSecret.lastRotatedAt && (
          <p className="text-subtle mt-2 text-[0.625rem]">
            Last rotated {formatTs(data.signingSecret.lastRotatedAt)}. Rotating
            invalidates all existing webhook signatures immediately.
          </p>
        )}
      </div>

      <NetSuiteRouting id={id} />
      {/* NetSuite Signing Key (RSA private key for the RESTlet adapter) */}
      <NetSuiteSigningKey id={id} />

      {/* SuiteApp Install */}
      <div className="bg-bg-elevated border-primary/8 rounded-lg border p-5">
        <span className="text-subtle text-xs font-semibold tracking-widest uppercase">
          SuiteApp Install
        </span>

        {/* Desktop: 4-col grid */}
        <div className="mt-4 hidden grid-cols-4 gap-4 sm:grid">
          {[
            { label: 'Bundle ID', value: data.suiteApp.bundleId },
            {
              label: 'Installed',
              value: data.suiteApp.installedVersion ?? '—',
              highlight:
                data.suiteApp.installedVersion !== null &&
                data.suiteApp.installedVersion !==
                  data.suiteApp.requiredVersion,
            },
            { label: 'Required', value: data.suiteApp.requiredVersion },
            {
              label: 'Install date',
              value: formatTs(data.suiteApp.installDate),
            },
          ].map(({ label, value, highlight }) => (
            <div key={label}>
              <p className="text-muted text-xs">{label}</p>
              <p
                className={[
                  'mt-1 text-sm font-medium',
                  highlight ? 'text-yellow-400' : 'text-primary',
                ].join(' ')}
              >
                {value}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile: row list with dividers */}
        <div className="mt-2 sm:hidden">
          {[
            { label: 'Bundle ID', value: data.suiteApp.bundleId },
            {
              label: 'Installed',
              value: data.suiteApp.installedVersion ?? '—',
              highlight:
                data.suiteApp.installedVersion !== null &&
                data.suiteApp.installedVersion !==
                  data.suiteApp.requiredVersion,
            },
            { label: 'Required', value: data.suiteApp.requiredVersion },
            {
              label: 'Install date',
              value: formatTs(data.suiteApp.installDate),
            },
          ].map(({ label, value, highlight }) => (
            <div
              key={label}
              className="border-primary/8 flex items-center justify-between border-b py-3 last:border-b-0"
            >
              <span className="text-muted text-sm">{label}</span>
              <span
                className={[
                  'text-sm font-medium',
                  highlight ? 'text-yellow-400' : 'text-primary',
                ].join(' ')}
              >
                {value}
              </span>
            </div>
          ))}

          {/* View details toggle */}
          <button
            onClick={() => setDetailsOpen((v) => !v)}
            className="border-primary/8 flex w-full items-center justify-between border-t pt-3"
          >
            <span className="text-primary text-sm font-medium">
              View details
            </span>
            <CircleChevronDown
              className={[
                'text-primary transition-transform duration-200',
                detailsOpen ? 'rotate-180' : '',
              ].join(' ')}
              strokeWidth={1.5}
              size={22}
            />
          </button>
        </div>

        {/* Install instructions — always visible on desktop, toggled on mobile */}
        <div
          className={[
            'border-primary/8 mt-5 rounded border p-4',
            detailsOpen ? '' : 'hidden sm:block',
          ].join(' ')}
        >
          <p className="text-subtle mb-3 text-xs font-semibold tracking-widest uppercase">
            SuiteApp Install Instructions · Bundle {data.suiteApp.bundleId}
          </p>
          <div className="space-y-1.5 rounded bg-black p-4">
            {[
              {
                key: 'API_ENDPOINT',
                value: data.installInstructions.apiEndpoint,
              },
              {
                key: 'CUSTOMER_ID',
                value: data.installInstructions.customerId,
              },
              {
                key: 'SIGNING_SECRET',
                value: data.signingSecret.maskedValue ?? '—',
              },
              { key: 'BUNDLE_ID', value: data.installInstructions.bundleId },
            ].map(({ key, value }) => (
              <div
                key={key}
                className="flex items-baseline justify-between gap-4"
              >
                <span className="text-subtle w-36 shrink-0 font-mono text-xs">
                  {key}
                </span>
                <span className="text-primary/50 text-right font-mono text-xs break-all">
                  {value}
                </span>
              </div>
            ))}
          </div>
          <p className="text-subtle mt-3 text-[0.625rem]">
            This block pulls from the editable content template (Settings →
            Install instructions). Auto-updates when the bundle moves to managed
            distribution.
          </p>
        </div>
      </div>
    </div>
  );
}
